<script setup lang="ts">
import type { FormkitCommonProps } from "@/components/ui/input/formkit/formkit.types";
import type { ISiteSubOwnerStatusType } from "@/types/backoffice/dto/reference/ref-site-sub-owner-status-type.ts";
import type { OwnerStatusId } from "@/types/common/ids";
import type { FormKitFrameworkContext } from "@formkit/core";
import type { PropType } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit";
import { useTypedIntl } from "@/locales/intl";
import {
  ESiteSubOwnerStatusType,
  getAllSiteSubOwnerStatusTypes,
} from "@/types/backoffice/dto/reference/ref-site-sub-owner-status-type.ts";

/**
 * @deprecated Use non-formkit-specific SiteOccupancyTypeSelect instead or its wrapped version SiteOccupancyTypeDropdownInput
 */
import Dropdown from "primevue/dropdown";

export type FormKitSiteOccupancyTypeDropdownProps = FormkitCommonProps;

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext & FormKitSiteOccupancyTypeDropdownProps>,
  },
});

const ALL_VALUES: ISiteSubOwnerStatusType[] = getAllSiteSubOwnerStatusTypes();
const { t, k } = useTypedIntl();

const value = useFormkitComputedValue<OwnerStatusId>(props.context);

function optionLabel(o: ISiteSubOwnerStatusType): string {
  const knownValue = ALL_VALUES.find((item) => item.id === o.id);

  return knownValue
    ? t(k.site.occupancyTypes[knownValue.description])
    : t(k.site.occupancyTypes[ESiteSubOwnerStatusType.UNDEFINED]);
}

function optionValue(o: ISiteSubOwnerStatusType): OwnerStatusId {
  return o.id;
}
</script>

<template>
  <Dropdown
    v-model="value"
    :options="ALL_VALUES"
    :name="props.context?.name"
    :option-label="optionLabel"
    :option-value="optionValue"
    :placeholder="props.context?.placeholder"
    class="w-full"
    :disabled="props.context?.disabled"
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.siteOccupancyTypeDropdown.dropdown()"
  />
</template>

<style scoped></style>
